import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { PERMISSIONS, SUBSCRIPTION_STATUSES } from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { PageSpinner } from '../../../components/PageSpinner';
import { applicationsApi } from '../../../features/applications/applications.api';
import { useAuth } from '../../../features/auth/auth-context';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { formatStatusLabel } from '../../../lib/format-status';
import { routeTo } from '../../../routes/paths';

import styles from './AdminApplicationsPage.module.css';

type IconName =
  'application' | 'arrow' | 'check' | 'clock' | 'filter' | 'message' | 'review' | 'spark';

function Icon({ name }: { name: IconName }): React.JSX.Element {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
    case 'application':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...common} d="M6 3h9l4 4v14H6zM15 3v5h4M9 12h7M9 16h5" />
        </svg>
      );
    case 'arrow':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...common} d="M5 12h14M14 7l5 5-5 5" />
        </svg>
      );
    case 'check':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle {...common} cx="12" cy="12" r="9" />
          <path {...common} d="m8 12 2.6 2.6L16.5 9" />
        </svg>
      );
    case 'clock':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle {...common} cx="12" cy="12" r="9" />
          <path {...common} d="M12 7v5l3 2" />
        </svg>
      );
    case 'filter':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...common} d="M4 6h16M7 12h10M10 18h4" />
        </svg>
      );
    case 'message':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...common} d="M4 5h16v12H8l-4 4zM8 9h8M8 13h5" />
        </svg>
      );
    case 'review':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle {...common} cx="11" cy="11" r="7" />
          <path {...common} d="m20 20-4-4M8 11l2 2 4-4" />
        </svg>
      );
    case 'spark':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...common} d="m13 2-8 12h7l-1 8 8-12h-7z" />
        </svg>
      );
  }
}

function conciseReference(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8).toUpperCase()}…` : id.toUpperCase();
}

function formatDate(value: string | null): string {
  if (value === null) return 'Not submitted';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unavailable';
  return date.toLocaleDateString('en-NG', { day: '2-digit', month: 'short', year: 'numeric' });
}

function statusTone(status: string): string {
  if (status === 'REJECTED' || status === 'CANCELLED' || status === 'DEFAULTED')
    return styles.statusDanger ?? '';
  if (status === 'CHANGES_REQUESTED' || status === 'SUSPENDED') return styles.statusWarning ?? '';
  if (status === 'ACTIVE' || status === 'COMPLETED' || status === 'MANDATE_ACTIVE')
    return styles.statusSuccess ?? '';
  if (status === 'UNDER_REVIEW' || status === 'SUBMITTED') return styles.statusReview ?? '';
  return styles.statusNeutral ?? '';
}

const METRIC_TONES = [
  styles.metricGreen,
  styles.metricBlue,
  styles.metricAmber,
  styles.metricMint,
  styles.metricViolet,
];

export function AdminApplicationsPage(): React.JSX.Element {
  const { can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState(() => {
    const requested = searchParams.get('status');
    return requested !== null && SUBSCRIPTION_STATUSES.some((item) => item === requested)
      ? requested
      : '';
  });

  const applications = useQuery({
    queryKey: ['admin', 'applications', { page, status }],
    queryFn: () =>
      applicationsApi.listAdmin({ page, pageSize: 20, status: status === '' ? undefined : status }),
  });
  const pipeline = useQuery({
    queryKey: ['admin', 'applications', 'pipeline'],
    queryFn: () => applicationsApi.getAdminPipeline(),
  });
  const reviewQueue = useQuery({
    queryKey: ['admin', 'applications', 'review-queue'],
    queryFn: () => applicationsApi.getAdminReviewQueue({ page: 1, pageSize: 5 }),
  });

  const metrics =
    pipeline.data === undefined
      ? []
      : [
          { label: 'Open journeys', value: pipeline.data.openCount, icon: 'application' as const },
          {
            label: 'Awaiting review',
            value: pipeline.data.awaitingReviewCount,
            icon: 'review' as const,
          },
          {
            label: 'Changes requested',
            value: pipeline.data.changesRequestedCount,
            icon: 'message' as const,
          },
          { label: 'Approved', value: pipeline.data.approvedCount, icon: 'check' as const },
          {
            label: 'Awaiting acceptance',
            value: pipeline.data.awaitingAcceptanceCount,
            icon: 'clock' as const,
          },
        ];
  const hasReviewPermission =
    can(PERMISSIONS.SUBSCRIPTIONS_APPROVE) || can(PERMISSIONS.SUBSCRIPTIONS_UPDATE);

  const clearFilter = (): void => {
    setStatus('');
    setPage(1);
    setSearchParams({});
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Application operations</p>
          <h1>Applications</h1>
          <p>Review customer requests, monitor every stage and keep solar journeys moving.</p>
        </div>
        <div className={styles.headerContext}>
          <span className={styles.headerIcon}>
            <Icon name="spark" />
          </span>
          <span>
            <strong>Review workspace</strong>
            <small>Backend-authoritative application data</small>
          </span>
        </div>
      </header>

      <section className={styles.metrics} aria-label="Application pipeline summary">
        {pipeline.isPending && (
          <div className={styles.metricsLoading}>
            <PageSpinner label="Loading pipeline summary" />
          </div>
        )}
        {pipeline.isError && (
          <div className={styles.metricsError}>
            <Alert tone="error">
              Pipeline summary is unavailable.{' '}
              <Button variant="secondary" onClick={() => void pipeline.refetch()}>
                Retry
              </Button>
            </Alert>
          </div>
        )}
        {pipeline.isSuccess &&
          metrics.map((metric, index) => (
            <article className={`${styles.metricCard} ${METRIC_TONES[index]}`} key={metric.label}>
              <span className={styles.metricIcon}>
                <Icon name={metric.icon} />
              </span>
              <span className={styles.metricBody}>
                <small>{metric.label}</small>
                <strong>{metric.value.toLocaleString('en-NG')}</strong>
              </span>
            </article>
          ))}
      </section>

      <section className={styles.filterPanel} aria-labelledby="application-filter-heading">
        <div className={styles.filterHeading}>
          <span className={styles.filterIcon}>
            <Icon name="filter" />
          </span>
          <div>
            <h2 id="application-filter-heading">Filter applications</h2>
            <p>Use the supported lifecycle status to narrow the register.</p>
          </div>
        </div>
        <label className={styles.filterLabel} htmlFor="application-status">
          <span>Status</span>
          <select
            id="application-status"
            value={status}
            onChange={(event) => {
              const nextStatus = event.target.value;
              setStatus(nextStatus);
              setPage(1);
              setSearchParams(nextStatus === '' ? {} : { status: nextStatus });
            }}
          >
            <option value="">All lifecycle statuses</option>
            {SUBSCRIPTION_STATUSES.map((option) => (
              <option key={option} value={option}>
                {formatStatusLabel(option)}
              </option>
            ))}
          </select>
        </label>
        <Button
          className={styles.clearButton}
          variant="secondary"
          disabled={status === ''}
          onClick={clearFilter}
        >
          Clear filter
        </Button>
      </section>

      <div className={styles.contentGrid}>
        <main className={styles.registerCard}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionIcon}>
              <Icon name="application" />
            </span>
            <div>
              <h2>Application register</h2>
              <p>
                {applications.data === undefined
                  ? 'Loading customer applications'
                  : `${applications.data.pagination.totalItems.toLocaleString('en-NG')} application${applications.data.pagination.totalItems === 1 ? '' : 's'}`}
              </p>
            </div>
          </div>

          {applications.isPending && <PageSpinner label="Loading applications" />}
          {applications.isError && (
            <div className={styles.inlineState}>
              <Alert tone="error">
                {toDisplayMessage(applications.error)}{' '}
                <Button variant="secondary" onClick={() => void applications.refetch()}>
                  Retry
                </Button>
              </Alert>
            </div>
          )}
          {applications.isSuccess && applications.data.items.length === 0 && (
            <div className={styles.emptyState}>
              <span>
                <Icon name="check" />
              </span>
              <h3>No applications found</h3>
              <p>
                {status === ''
                  ? 'The register is empty.'
                  : 'No applications match this lifecycle status.'}
              </p>
              {status !== '' && (
                <Button variant="secondary" onClick={clearFilter}>
                  Show all applications
                </Button>
              )}
            </div>
          )}
          {applications.isSuccess && applications.data.items.length > 0 && (
            <>
              <div className={styles.tableWrapper}>
                <table className={styles.table} aria-label="Application register">
                  <thead>
                    <tr>
                      <th scope="col">Application</th>
                      <th scope="col">Customer ref</th>
                      <th scope="col">Product &amp; plan</th>
                      <th scope="col">Requested finance</th>
                      <th scope="col">Timeline</th>
                      <th scope="col">Status</th>
                      <th scope="col">
                        <span className={styles.visuallyHidden}>Action</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.data.items.map((application) => {
                      const requested = application.requestedTerms ?? null;
                      const reference =
                        application.contractReference?.trim() || conciseReference(application.id);
                      return (
                        <tr key={application.id}>
                          <td data-label="Application">
                            <strong className={styles.reference} title={application.id}>
                              {reference}
                            </strong>
                            <span className={styles.secondaryText}>
                              Version {application.version}
                            </span>
                          </td>
                          <td data-label="Customer ref">
                            <Link
                              className={styles.customerLink}
                              to={routeTo.adminCustomer(application.customerId)}
                              title={application.customerId}
                            >
                              {conciseReference(application.customerId)}
                            </Link>
                          </td>
                          <td data-label="Product & plan">
                            <strong className={styles.productName}>
                              {requested?.productName ??
                                application.productNameSnapshot ??
                                'Product unavailable'}
                            </strong>
                            <span className={styles.secondaryText}>
                              {requested?.planName ?? 'Plan unavailable'}
                            </span>
                          </td>
                          <td data-label="Requested finance">
                            <strong className={styles.money}>
                              {formatNgn(requested?.financedAmount ?? application.financedAmount)}
                            </strong>
                          </td>
                          <td data-label="Timeline">
                            <span className={styles.dateText}>
                              {formatDate(application.submittedAt)}
                            </span>
                            <span className={styles.secondaryText}>
                              Updated {formatDate(application.updatedAt)}
                            </span>
                          </td>
                          <td data-label="Status">
                            <span
                              className={`${styles.statusBadge} ${statusTone(application.status)}`}
                            >
                              {formatStatusLabel(application.status)}
                            </span>
                          </td>
                          <td className={styles.rowAction} data-label="Action">
                            <Link
                              to={routeTo.adminApplication(application.id)}
                              aria-label={`Open application ${reference}`}
                            >
                              Open <Icon name="arrow" />
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <nav className={styles.pagination} aria-label="Application pages">
                <Button
                  variant="secondary"
                  disabled={!applications.data.pagination.hasPreviousPage}
                  onClick={() => setPage((current) => current - 1)}
                >
                  Previous
                </Button>
                <span>
                  Page {applications.data.pagination.page} of{' '}
                  {applications.data.pagination.totalPages}
                </span>
                <Button
                  variant="secondary"
                  disabled={!applications.data.pagination.hasNextPage}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                </Button>
              </nav>
            </>
          )}
        </main>

        <aside className={styles.insightRail} aria-label="Application insights">
          <section className={styles.railCard}>
            <div className={styles.railHeader}>
              <span className={styles.sectionIcon}>
                <Icon name="review" />
              </span>
              <div>
                <h2>Review queue</h2>
                <p>Oldest waiting first</p>
              </div>
            </div>
            {reviewQueue.isPending && <PageSpinner label="Loading review queue" />}
            {reviewQueue.isError && (
              <div className={styles.railState}>
                <Alert tone="error">
                  Review queue is unavailable.{' '}
                  <Button variant="secondary" onClick={() => void reviewQueue.refetch()}>
                    Retry
                  </Button>
                </Alert>
              </div>
            )}
            {reviewQueue.isSuccess && reviewQueue.data.items.length === 0 && (
              <div className={styles.queueEmpty}>
                <Icon name="check" />
                <span>
                  <strong>Queue clear</strong>
                  <small>No applications are waiting for staff review.</small>
                </span>
              </div>
            )}
            {reviewQueue.isSuccess && reviewQueue.data.items.length > 0 && (
              <ol className={styles.queueList}>
                {reviewQueue.data.items.map((item) => (
                  <li key={item.subscriptionId}>
                    <Link to={routeTo.adminApplication(item.subscriptionId)}>
                      <span className={styles.queueAvatar} aria-hidden="true">
                        {item.customerDisplayName.trim().charAt(0).toUpperCase() || 'C'}
                      </span>
                      <span className={styles.queueBody}>
                        <strong>{item.customerDisplayName || 'Customer name unavailable'}</strong>
                        <small>
                          {item.productName ?? 'Product unavailable'} ·{' '}
                          {formatStatusLabel(item.status)}
                        </small>
                        <em>
                          {item.waitingAgeDays === null
                            ? 'Waiting time unavailable'
                            : item.waitingAgeDays === 0
                              ? 'Waiting since today'
                              : `Waiting ${item.waitingAgeDays} day${item.waitingAgeDays === 1 ? '' : 's'}`}
                        </em>
                      </span>
                      <Icon name="arrow" />
                    </Link>
                  </li>
                ))}
              </ol>
            )}
          </section>

          <section className={styles.railCard}>
            <div className={styles.railHeader}>
              <span className={styles.sectionIcon}>
                <Icon name="spark" />
              </span>
              <div>
                <h2>Applications by stage</h2>
                <p>Full lifecycle pipeline</p>
              </div>
            </div>
            {pipeline.isSuccess && (
              <div className={styles.stageList}>
                {pipeline.data.stages.map((stage) => (
                  <div className={styles.stageRow} key={stage.status}>
                    <span className={styles.stageLabel}>{formatStatusLabel(stage.status)}</span>
                    <span className={styles.stageTrack} aria-hidden="true">
                      <span
                        style={{
                          width:
                            pipeline.data.pipelineTotal > 0 && stage.count > 0
                              ? `${Math.max(3, (stage.count / pipeline.data.pipelineTotal) * 100)}%`
                              : '0%',
                        }}
                      />
                    </span>
                    <strong>{stage.count.toLocaleString('en-NG')}</strong>
                  </div>
                ))}
                {pipeline.data.excludedFromPipeline.length > 0 && (
                  <div className={styles.excludedStages}>
                    <span>Outside active pipeline</span>
                    <div>
                      {pipeline.data.excludedFromPipeline.map((stage) => (
                        <small key={stage.status}>
                          {formatStatusLabel(stage.status)}{' '}
                          <strong>{stage.count.toLocaleString('en-NG')}</strong>
                        </small>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {pipeline.isPending && <PageSpinner label="Loading application stages" />}
            {pipeline.isError && (
              <p className={styles.unavailable}>Stage distribution unavailable.</p>
            )}
          </section>

          <section className={`${styles.railCard} ${styles.guidanceCard}`}>
            <span className={styles.guidanceIcon}>
              <Icon name="spark" />
            </span>
            <div>
              <h2>Review with confidence</h2>
              <p>
                Open an application to inspect its immutable requested terms before recording any
                supported decision.
              </p>
              <small>
                {hasReviewPermission
                  ? 'Your access includes application review actions.'
                  : 'Your access is read-only for application decisions.'}
              </small>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
